// frontend/src/components/tables/ManualBoxLinesTable.js
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import axios from 'axios';
import useManualStrategyActions from '../../hooks/useManualStrategyActions';
import StrategyControls, { strategyStatus } from '../common/StrategyControls';
import { boxLineColor } from '../../utils/boxLineStyle';
import { manualLineDeleteDisabledReason } from '../../utils/manualBoxLine';

const formatKST = (value) =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(value));

const ManualBoxLinesTable = (
  {
    daysDelta,
    rowsProp,
    selectedId,
    onSelect,
    onDeleted,
    control,
    onStrategyChanged,
  },
  ref,
) => {
  const [rows, setRows] = useState(rowsProp ?? []);
  const [error, setError] = useState('');
  const [selectedLineId, setSelectedLineId] = useState(null);
  const activeSelectedId = selectedLineId ?? selectedId;
  const requestId = useRef(0);
  const reload = useCallback(async () => {
    if (rowsProp !== undefined) return;
    const id = ++requestId.current;
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/boxlines/manual`,
        {
          params: { days: daysDelta },
        },
      );
      if (id === requestId.current) {
        setRows(data);
        setError('');
      }
    } catch (err) {
      if (id === requestId.current)
        setError('수동 라인을 조회하지 못했습니다.');
    }
  }, [daysDelta, rowsProp]);
  useImperativeHandle(ref, () => ({ reload }), [reload]);
  useEffect(() => {
    if (rowsProp !== undefined) {
      setRows(rowsProp);
      return;
    }
    setRows([]);
    reload();
    const timer = setInterval(reload, 5000);
    return () => {
      ++requestId.current;
      clearInterval(timer);
    };
  }, [reload, rowsProp]);
  useEffect(() => {
    setSelectedLineId((id) => (rows.some((row) => row.id === id) ? id : null));
  }, [rows]);

  const refresh = () => {
    if (onStrategyChanged) onStrategyChanged();
    else reload();
  };
  const { canManage, isManual, pendingId, message, handleStrategy } =
    useManualStrategyActions(control, refresh);
  const canEdit = (row) =>
    control?.can_manage &&
    control.trading_open &&
    !row.strategy &&
    Date.now() < Date.parse(control.session_end) &&
    Date.parse(row.timestamp_korean_box) >= Date.parse(control.session_start) &&
    Date.parse(row.timestamp_korean_box) < Date.parse(control.session_end);

  const selectLine = (row) => {
    if (!canManage || pendingId !== null) return;
    setSelectedLineId(row.id);
    // 연결 전략이 있는 행도 삭제 대상으로 선택하되, 가격 수정은 기존 제한을 유지한다.
    onSelect?.(canEdit(row) ? row : null);
  };
  const deleteLine = async (e, row) => {
    e.stopPropagation();
    setError('');
    if (await handleStrategy(row, 'delete-line')) {
      setRows((current) => current.filter((item) => item.id !== row.id));
      setSelectedLineId(null);
      onDeleted?.(row.id);
    }
  };
  const cell =
    'px-3 py-2 border border-neutral-300 whitespace-nowrap text-center';
  return (
    <div className='overflow-x-auto mb-4'>
      {(message || error) && (
        <p role='status' className='my-2'>
          {error || message}
        </p>
      )}
      <table className='mx-auto border-collapse'>
        <thead>
          <tr>
            {[
              '세션 날짜',
              '봉우리 1',
              '봉우리 2',
              '봉우리 3',
              '지수',
              '상단/하단',
              '전략 상태',
              ...(canManage ? ['전략 관리'] : []),
              ...(canManage ? ['라인 삭제'] : []),
            ].map((title) => (
              <th key={title} className={`${cell} bg-neutral-100`}>
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const color = boxLineColor(row);
            const selected = row.id === activeSelectedId;
            const deleteDisabledReason = manualLineDeleteDisabledReason(
              control,
              row,
            );
            return (
              <tr
                key={row.id}
                onClick={() => selectLine(row)}
                tabIndex={canManage ? 0 : undefined}
                aria-selected={selected}
                onKeyDown={(e) => {
                  if (
                    e.target === e.currentTarget &&
                    (e.key === 'Enter' || e.key === ' ')
                  ) {
                    e.preventDefault();
                    selectLine(row);
                  }
                }}
                className={`${canManage ? 'cursor-pointer' : ''} ${selected ? 'outline outline-2 outline-sky-500 bg-sky-50' : ''}`}
              >
                <td className={cell}>
                  {formatKST(row.span_start).slice(0, 10)}
                </td>
                <td className={cell}>
                  {formatKST(row.span_start).slice(11)}
                </td>
                <td className={cell}>
                  {formatKST(row.detected_at).slice(11)}
                </td>
                <td
                  className={cell}
                  title={formatKST(row.span_end)}
                >
                  {formatKST(row.span_end).slice(11)}
                </td>
                <td className={cell} style={{ color }}>
                  {row.close_box}
                </td>
                <td className={cell} style={{ color }}>
                  {row.is_top ? '상단' : '하단'}
                </td>
                <td className={cell}>
                  {strategyStatus(row.strategy, isManual)}
                </td>
                {canManage && (
                  <td className={cell}>
                    <StrategyControls
                      row={row}
                      control={control}
                      pendingId={pendingId}
                      onAction={handleStrategy}
                    />
                  </td>
                )}
                {canManage && (
                  <td className={cell}>
                    {selected && (
                      <button
                        type='button'
                        aria-label={`${row.close_box} 수동 라인과 연결 전략 삭제`}
                        title={
                          deleteDisabledReason ||
                          '수동 라인과 연결된 전략을 함께 삭제합니다.'
                        }
                        disabled={
                          pendingId !== null || Boolean(deleteDisabledReason)
                        }
                        className='inline-flex p-2 border rounded text-red-600 disabled:opacity-40'
                        onClick={(e) => deleteLine(e, row)}
                      >
                        <svg
                          aria-hidden='true'
                          width='18'
                          height='18'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6' />
                        </svg>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
export default forwardRef(ManualBoxLinesTable);
